import { Pipe, PipeTransform } from '@angular/core';
import { CellData, DataMap } from '@ddt-web/data-service';
import * as get from 'lodash/get';
import * as groupBy from 'lodash/groupBy';
import * as isEmpty from 'lodash/isEmpty';

import { ApplicationConstants } from '../config';
import { ModelsExperienceFilters } from '../models';

@Pipe({
	name: 'modelsBrowseFilter'
})
export class ModelsBrowseFilterPipe implements PipeTransform {

	/**
	 * Apply selected filters and reduce tableData rows based on selected filterId and options applicable
	 * Group the highlighted filters based on filterKey and apply the filters
	 * if filterId does not match rows - return all data
	 * 
	 * @param {DataMap<CellData>[]} rows
	 * @param {ModelsBrowseFilter[]} [selectedFilters]
	 * @param {string} [childColumnFilter] 
	 * For childColumnFilter - pass column name as arg, based on this column, family data gets filtered 
	 * e.g. For family list, we filter rows based on 'portfolios' column, 
	 * if selectedFilter 'objective' options matches any value in portfolios array, the family row is returned
	 * 
	 * @return {*}  {DataMap<CellData>[]}
	 * @memberof ModelsBrowseFilterPipe
	 */
	public transform(rows: DataMap<CellData>[], selectedFilters?: ModelsExperienceFilters[], childColumnFilter?: string): DataMap<CellData>[] {

		if (isEmpty(rows) || isEmpty(selectedFilters)) {
			return rows;
		}
		const formattedFilters = selectedFilters.filter(filter => filter.meFilterGroup !== ApplicationConstants.HIGHLIGHTED);

		let highlightedFilters = selectedFilters.filter(selectedFilter => selectedFilter.meFilterGroup === ApplicationConstants.HIGHLIGHTED);
		highlightedFilters = groupBy(highlightedFilters, ApplicationConstants.ME_FILTER_KEY);
		// combine same key highlighted filters options to get common options values (intersection of options values)
		Object.keys(highlightedFilters).forEach(filterId => {
			const filterOptionsList = highlightedFilters[filterId].map(filter => filter.options);
			const commonFilterOptions = filterOptionsList.reduce((a, b) => a.filter(c => b.includes(c))); // to get common options conjunction options b/w filters

			formattedFilters.push({
				meFilterId: filterId,
				meFilterKey: highlightedFilters[filterId]?.[0]?.meFilterKey ?? '',
				applicability: highlightedFilters[filterId]?.[0]?.applicability ?? '',
				options: commonFilterOptions
			});
		});
		let filteredRows = rows.filter(row => {
			const filterRow = formattedFilters.map(filter => {
				const options = filter?.options;
				const filterKey = filter?.meFilterKey ?? '';
				const applicability = filter?.applicability ?? '';

				const columnConfig = row?.[filterKey];
				const rawValue = columnConfig?.rawValue;

				// handle this case separately below
				if (childColumnFilter && applicability === ApplicationConstants.APPLICABILITY_PORTFOLIO) {
					return true;
				}

				if (rawValue) {
					return options.includes(rawValue as any);
				} else if (columnConfig) {
					return options.includes(columnConfig as any);
				} else if (options.length) {
					return true; // meaning the data point not available in the row 
				} else {
					return false; // if no options available and above conditions failed return false
				}
			});
			return filterRow.reduce((x, y) => x && y, true);
		});
		// if childColumnFilter, filter them at child column level 
		if (childColumnFilter) {
			const portfolioLevelFilters = selectedFilters.filter(selectedFilter => selectedFilter?.applicability === ApplicationConstants.APPLICABILITY_PORTFOLIO);

			filteredRows = filteredRows.filter(filterRow => {
				const childData = this.transform(get(filterRow, 'portfolios', [])?.rawValue, portfolioLevelFilters);
				return childData?.length;
			});

		}
		return filteredRows;
	}
}
